<template>
  <v-container
    :class="['department-daily-schedule pa-0', compact ? 'compact d-inline-block' : 'full']"
    fluid
  >
    <template v-if="compact">
      <v-row
        align="center"
        class="column-header px-2"
        no-gutters
      >
        <v-col
          class="font-weight-medium text-truncate department-name"
          :title="department.name"
        >
          {{ department.name }}
        </v-col>
        <v-col
          v-if="!retrievingSchedule && scheduleStateIndicator"
          cols="auto"
        >
          <v-chip
            :class="['font-weight-medium white--text', scheduleStateIndicator.stateLabelCssClass]"
            x-small
          >
            {{ $t(scheduleStateIndicator.stateLabelKey) }}
          </v-chip>
        </v-col>
        <v-col
          v-if="!retrievingSchedule && !readOnly && (showFinalizeButton || showFinalizeMessage)"
          cols="auto"
        >
          <v-tooltip
            bottom
            max-width="320px"
          >
            <template #activator="{ on, attrs }">
              <v-btn
                v-if="showFinalizeButton"
                class="finalize ml-1"
                color="secondary"
                :disabled="finalizing"
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click.prevent="finalizeScheduleChanges"
              >
                <v-progress-circular
                  v-if="finalizing"
                  color="primary lighten-2"
                  indeterminate
                  size="18"
                  width="2"
                />
                <v-icon
                  v-else
                  size="16"
                >
                  fas fa-paper-plane
                </v-icon>
              </v-btn>
              <v-icon
                v-else
                class="ml-1 finalized"
                color="info"
                size="14"
                v-bind="attrs"
                v-on="on"
              >
                fal fa-question-circle
              </v-icon>
            </template>
            <span class="body-2">
              {{ showFinalizeButton ? $t('labels.finalize') : $t('descriptions.dailyScheduleFinalizedHint') }}
            </span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-divider />
    </template>
    <template v-else-if="!retrievingSchedule">
      <portal to="daily-schedule-finalize">
        <template v-if="!readOnly">
          <v-btn
            v-if="showFinalizeButton"
            class="finalize mb-1 ml-2"
            color="secondary"
            :disabled="finalizing"
            :text="$vuetify.breakpoint.lgAndUp"
            :icon="$vuetify.breakpoint.mdAndDown"
            :title="$t('labels.finalize')"
            @click.prevent="finalizeScheduleChanges"
          >
            <v-progress-circular
              v-if="finalizing"
              color="primary lighten-2"
              indeterminate
              size="22"
              width="2"
            />
            <v-icon
              v-else
              size="20"
            >
              fas fa-paper-plane
            </v-icon>
            <span
              v-if="$vuetify.breakpoint.lgAndUp"
              class="ml-2"
            >
              {{ $t('labels.finalize') }}
            </span>
          </v-btn>
          <span
            v-else-if="showFinalizeMessage"
            class="caption font-weight-medium ml-2"
          >
            {{ $vuetify.breakpoint.lgAndDown ? '': $t('descriptions.dailyScheduleFinalized') }}
            <v-tooltip
              max-width="320px"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-icon
                  class="mt-n1"
                  color="info"
                  size="14"
                  v-bind="attrs"
                  v-on="on"
                >
                  fal fa-question-circle
                </v-icon>
              </template>
              <span>
                {{ $t('descriptions.dailyScheduleFinalizedHint') }}
              </span>
            </v-tooltip>
          </span>
        </template>
      </portal>
      <portal to="daily-schedule-state">
        <template v-if="scheduleStateIndicator">
          <v-chip
            :class="['font-weight-medium ml-5 white--text', scheduleStateIndicator.stateLabelCssClass]"
            small
          >
            {{ $t(scheduleStateIndicator.stateLabelKey) }}
          </v-chip>
          <span
            v-if="readOnly"
            :class="['caption font-weight-medium ml-3 grey--text text--darken-1']"
          >
            {{ $t('labels.readOnlyMode') }}
          </span>
        </template>
      </portal>
    </template>
    <template v-if="retrievingSchedule && showSkeletonLoader">
      <v-container
        class="pa-0"
        fluid
      >
        <v-row dense>
          <v-spacer />
          <v-col class="text-center">
            <v-btn
              disabled
              :elevation="0"
              fab
              small
            />
          </v-col>
          <v-col class="text-center">
            <v-btn
              disabled
              :elevation="0"
              fab
              small
            />
          </v-col>
          <v-col class="text-center">
            <v-btn
              disabled
              :elevation="0"
              fab
              small
            />
          </v-col>
          <v-spacer />
        </v-row>
        <v-divider />
        <v-row dense>
          <v-col>
            <v-skeleton-loader
              class="single-action"
              type="list-item-avatar"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <v-skeleton-loader
              class="single-action"
              type="list-item-avatar"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <v-skeleton-loader
              class="single-action"
              type="list-item-avatar"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <v-skeleton-loader
              class="single-action"
              type="list-item-avatar"
            />
          </v-col>
        </v-row>
      </v-container>
    </template>
    <template v-else-if="!retrievingSchedule">
      <v-container
        class="pa-0"
        fluid
      >
        <v-container
          class="body-container pa-0"
          fluid
        >
          <v-row
            v-if="!compact"
            dense
            no-gutters
          >
            <v-col
              cols="12"
            >
              <v-tabs
                v-model="selectedJobTypeTab"
                class="job-type-tabs"
                mobile-breakpoint="768"
                show-arrows
                slider-color="accent"
                slider-size="3"
              >
                <v-tab
                  v-for="(jobTypeGroup, index) in jobTypes"
                  :key="index"
                >
                  <template
                    v-for="(jobType, jobIndex) in jobTypeGroup"
                  >
                    <span
                      v-if="jobIndex > 0"
                      :key="`${jobType.id}-plus`"
                      class="ml-2"
                    >
                      +
                    </span>
                    <span
                      :key="`${jobType.id}-name`"
                      :class="jobIndex > 0 ? 'ml-2' : ''"
                    >
                      {{ jobType.name }}
                    </span>
                    <div
                      :key="`${jobType.id}-count`"
                      :class="['font-weight-bold job-count ml-2 px-1', ratioCount[jobType.id] && ratioCount[jobType.id] !== jobTypeCount[jobType.id] ? 'imbalance' : '']"
                    >
                      <span>
                        {{ jobTypeCount[jobType.id] }}
                      </span>
                    </div>
                    <div
                      v-if="jobType.id !== ALL_JOB_TYPES_ID && ratioCount[jobType.id] && ratioCount[jobType.id] !== jobTypeCount[jobType.id]"
                      :key="`${jobType.id}-ratio`"
                      :class="['font-weight-bold job-count ml-2 px-1 ratio']"
                    >
                      <span>
                        {{ ratioCount[jobType.id] }}
                      </span>
                    </div>
                  </template>
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
          <v-data-table
            class="categories"
            dense
            fixed-header
            group-by="category"
            :headers="headers"
            :header-props="{ sortIcon: 'fa fa-arrow-up' }"
            :height="`${tableHeight}px`"
            hide-default-footer
            :items="filteredItems"
            :items-per-page="filteredItems.length"
            mobile-breakpoint=""
            :no-data-text="$t('descriptions.noShifts')"
            :sort-by="sort.columns"
            :sort-desc="sort.order"
            @update:sort-by="setSortBy"
            @update:sort-desc="setSortDirection"
          >
            <template #item="{ item: categoryItem }">
              <td
                class="category-cell"
                :colspan="headers.length"
              >
                <v-data-table
                  v-model="selectedRows"
                  :class="['schedule-items', selectedJobTypeTab === ALL_JOB_TYPES_ID || jobTypes[selectedJobTypeTab].length > 1 ? 'multiple' : 'single']"
                  dense
                  fixed-header
                  group-by="jobTypeIndex"
                  :headers="headers"
                  hide-default-footer
                  item-class="type"
                  :items="categoryItem.activities"
                  :items-per-page="categoryItem.activities.length"
                  mobile-breakpoint=""
                  :no-data-text="$t('descriptions.noShifts')"
                  :show-expand="!compact"
                  single-select
                  @click:row="showItemDetails"
                >
                  <template #group.header="{ group: jobTypeIndex }">
                    <td
                      :colspan="headers.length"
                    >
                      <span
                        class="pl-1"
                      >
                        {{ getJobLabel(jobTypeIndex) }}
                      </span>
                      <div
                        class="font-weight-bold job-count ml-2 px-1 inner-table"
                      >
                        <span>
                          {{ categoryItem.category === categoryMapping.onDuty ? getJobCount(jobTypeIndex) : getCategoryCount(categoryItem.category, jobTypeIndex) }}
                        </span>
                      </div>
                      <div
                        v-if="categoryItem.category === categoryMapping.onDuty"
                        class="font-weight-bold job-count ml-2 px-1 inner-table"
                      >
                        <span>
                          {{ `${$t('labels.hours')}: ${getJobHours(jobTypeIndex)}` }}
                        </span>
                      </div>
                    </td>
                  </template>
                  <template #item.assigneeId="{ item }">
                    <v-list-item class="px-0">
                      <v-list-item-avatar
                        v-if="!compact"
                        class="mr-1"
                      >
                        <v-avatar
                          :color="$store.state.org.employees[item.user.userId].avatarBgColor"
                          size="30"
                        >
                          <span class="white--text subtitle-2">
                            {{ getAvatar($store.state.org.employees[item.user.userId]) }}
                          </span>
                        </v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content :class="compact ? 'py-1' : ''">
                        <v-list-item-title
                          :class="[compact ? 'body-2 mb-0 name-n-avatar' : 'body-2 mb-1 name-n-avatar', item.user.state === ACCOUNT_STATE.active ? '' : 'grey--text']"
                          :title="item.user.fullName"
                        >
                          <span
                            class="d-inline-block text-truncate"
                            :style="{ width: compact ? '168px' : '134px' }"
                          >
                            <UserName
                              :user="$store.state.org.employees[item.user.userId]"
                              @saved="updateNurseDetails"
                            />
                          </span>
                        </v-list-item-title>
                        <v-list-item-subtitle
                          :class="[compact ? 'caption mt-0 job-info' : 'caption mt-1', item.user.state === ACCOUNT_STATE.active ? '' : 'grey--text']"
                          :title="showFlagChips(item) ? getJobAndFlagsText(item) : null"
                        >
                          {{ getJobInfo(item) }}
                          <!-- On one line with the job info; flags that don't fit are cut off with an ellipsis. -->
                          <span
                            v-if="showFlagChips(item)"
                            class="flag-chips ml-1"
                          >
                            <v-chip
                              v-for="flag in getShiftFlags(item)"
                              :key="flag.id"
                              class="lighten-2 grey--text text--darken-3 flag-short-code mr-1"
                              color="info"
                              x-small
                              :title="flag.name"
                            >
                              {{ flag.shortCode }}
                            </v-chip>
                          </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-tooltip
                        nudge-left="5"
                        right
                      >
                        <template #activator="{ on: tooltipOn, attrs }">
                          <v-btn
                            v-if="openedPanelName === 'nurse-details' && panels[0] && panels[0].userId === item.user.id"
                            class="info darken-1 staff-notes"
                            icon
                            x-small
                            v-bind="attrs"
                            v-on="tooltipOn"
                            @click.prevent.stop="toggleNurseDetailsPanel(item)"
                          >
                            <v-icon
                              class="white--text"
                              size="10"
                            >
                              fal fa-sliders-h fa-inverse
                            </v-icon>
                          </v-btn>
                          <v-btn
                            v-else-if="item.user.scheduleNotes"
                            class="secondary lighten-4 staff-notes"
                            icon
                            x-small
                            v-bind="attrs"
                            v-on="tooltipOn"
                            @click.prevent.stop="toggleNurseDetailsPanel(item)"
                          >
                            <v-icon
                              class="anchor--text"
                              size="10"
                            >
                              fal fa-sliders-h
                            </v-icon>
                          </v-btn>
                          <v-btn
                            v-else
                            class="grey lighten-3 staff-notes"
                            icon
                            x-small
                            v-bind="attrs"
                            v-on="tooltipOn"
                            @click.prevent.stop="toggleNurseDetailsPanel(item)"
                          >
                            <v-icon
                              class="grey--text text--darken-3"
                              size="10"
                            >
                              fal fa-sliders-h
                            </v-icon>
                          </v-btn>
                        </template>
                        <span class="body-2">
                          {{ $t('labels.viewStaffDetails') }}
                        </span>
                      </v-tooltip>
                      <v-tooltip
                        v-if="getErrors(item.user).length > 0"
                        nudge-left="5"
                        right
                        max-width="300px"
                      >
                        <template #activator="{ on: tooltipOn, attrs }">
                          <v-icon
                            class="has-errors"
                            color="error"
                            x-small
                            v-bind="attrs"
                            v-on="tooltipOn"
                          >
                            fal fa-exclamation-triangle
                          </v-icon>
                        </template>
                        <span class="body-2">
                          {{ $t('descriptions.staffErrors') }}
                        </span>
                      </v-tooltip>
                    </v-list-item>
                  </template>
                  <template #item.typeId="{ item }">
                    <v-row
                      v-if="item.value && item.type === 'shift'"
                      align="center"
                      :class="['shift-type', 'pa-0', item.value.overtime ? 'error--text' : '']"
                      justify="center"
                      no-gutters
                      style="position: relative"
                    >
                      <template v-if="item.value.departmentId !== activeDepartment.id || hasDefaultShiftTime(item.value)">
                        <span v-if="item.value.departmentId !== activeDepartment.id">
                          {{ getDepartmentByID(item.value.departmentId).name }}
                        </span>
                        <template v-else>
                          <ScheduleSymbol
                            :symbol="getSymbol('shift', item)"
                            :entity="shiftTypes[item.value.typeId]"
                          />
                          <span v-if="item.value.obligatory">
                            *
                          </span>
                        </template>
                        <span
                          v-if="item.category === categoryMapping.nonDuty"
                          class="non-duty"
                        />
                      </template>
                      <template v-else>
                        <span
                          class="caption"
                        >
                          <span class="d-block">
                            {{ getShiftTime(item.value)[0] + (item.value.obligatory ? '*' : '') }}
                          </span>
                          <span class="d-block">
                            {{ getShiftTime(item.value)[1] }}
                          </span>
                        </span>
                        <span
                          v-if="item.category === categoryMapping.nonDuty"
                          class="non-duty custom-time"
                        />
                      </template>
                    </v-row>
                    <v-icon
                      v-if="item.value && item.type === 'shift' && wasShiftModifiedByManagement(item.value, $store.state)"
                      class="management"
                      color="nb-gold"
                      size="10"
                    >
                      fas fa-pencil
                    </v-icon>
                    <v-icon
                      v-if="item.value && item.type === 'shift' && hasDifferentPayrollDate(item)"
                      class="payroll-diff text--darken-3"
                      color="grey"
                      size="10"
                    >
                      fas fa-level-up
                    </v-icon>
                    <v-icon
                      v-if="item.value && item.request"
                      class="request"
                      color="warning"
                      size="10"
                    >
                      fas fa-circle
                    </v-icon>
                    <v-row
                      v-if="item.value && item.type === 'event'"
                      align-content="end"
                      no-gutters
                      style="height: 100%;"
                      :title="getEventType(item.value).name"
                    >
                      <ScheduleSymbol
                        class="ml-3 mb-1"
                        :symbol="{ symbolType: 'bar', color: getEventType(item.value).color, style: { height: '5px !important', width: '32px !important' } }"
                      />
                    </v-row>
                  </template>
                  <template #item.flags="{ item }">
                    <div
                      v-if="item.value && item.type === 'shift'"
                      class="text-truncate"
                      :style="{ width: `200px`}"
                    >
                      <v-chip
                        v-for="flagId in item.value.flags"
                        :key="flagId"
                        class="lighten-2 grey--text text--darken-3 flag-short-code mb-1 mr-1"
                        color="info"
                        small
                        :title="shiftFlagsById[flagId].name"
                      >
                        {{ shiftFlagsById[flagId].shortCode }}
                      </v-chip>
                    </div>
                  </template>
                  <template #item.comments="{ item, header }">
                    <span
                      v-if="item.value"
                      class="d-inline-block text-truncate"
                      :style="{ width: `${header.width - 32}px`}"
                      :title="item.value.comments"
                    >
                      {{ item.value.comments }}
                    </span>
                  </template>
                  <template #item.lastFloat="{ item, header }">
                    <span
                      v-if="item.value && lastFloat.users[item.value.assigneeId] && lastFloat.users[item.value.assigneeId].last"
                      class="d-inline-block text-truncate"
                      :style="{ width: `${header.width - 32}px`}"
                    >
                      {{ moment(lastFloat.users[item.value.assigneeId].last.payrollDate || lastFloat.users[item.value.assigneeId].last.date).format(DATE_FORMAT_US) }}
                    </span>
                    <v-progress-circular
                      v-else-if="item.value && lastFloat.loading"
                      color="primary lighten-2"
                      indeterminate
                      size="18"
                      width="2"
                    />
                  </template>
                  <template #item.lastCancel="{ item, header }">
                    <span
                      v-if="item.value && lastCanceled.users[item.value.assigneeId] && lastCanceled.users[item.value.assigneeId].last"
                      class="d-inline-block text-truncate"
                      :style="{ width: `${header.width - 32}px`}"
                    >
                      {{ moment(lastCanceled.users[item.value.assigneeId].last.payrollDate || lastCanceled.users[item.value.assigneeId].last.date).format(DATE_FORMAT_US) }}
                    </span>
                    <v-progress-circular
                      v-else-if="item.value && lastCanceled.loading"
                      color="primary lighten-2"
                      indeterminate
                      size="18"
                      width="2"
                    />
                  </template>
                  <template #item.lastOvertime="{ item, header }">
                    <span
                      v-if="item.value && lastOvertime.users[item.value.assigneeId] && lastOvertime.users[item.value.assigneeId].last"
                      class="d-inline-block text-truncate"
                      :style="{ width: `${header.width - 32}px`}"
                    >
                      {{ moment(lastOvertime.users[item.value.assigneeId].last.payrollDate || lastOvertime.users[item.value.assigneeId].last.date).format(DATE_FORMAT_US) }}
                    </span>
                    <v-progress-circular
                      v-else-if="item.value && lastOvertime.loading"
                      color="primary lighten-2"
                      indeterminate
                      size="18"
                      width="2"
                    />
                  </template>
                  <template #item.data-table-expand="{ expand, isExpanded }">
                    <v-btn
                      icon
                      small
                      @click.stop.prevent="expand(!isExpanded)"
                    >
                      <v-icon
                        small
                      >
                        {{ isExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:expanded-item="{ headers: innerHeaders, item }">
                    <td
                      v-if="item.value"
                      class="px-0"
                      :colspan="innerHeaders.length"
                    >
                      <v-container class="last-shift-browser mx-0 pr-0 py-0">
                        <v-row>
                          <v-col
                            class="pa-0"
                          >
                            <LastShiftBrowser
                              :user="item.user"
                            />
                          </v-col>
                        </v-row>
                      </v-container>
                    </td>
                  </template>
                </v-data-table>
                <span
                  v-if="readOnly"
                  class="read-only-overlay"
                />
              </td>
            </template>
            <template #group.header="{ group, isOpen, toggle }">
              <td
                :ref="group"
                :colspan="headers.length"
                :class="isOpen ? 'expanded' : ''"
                @click="expandGroup(group, isOpen, toggle)"
              >
                <v-icon
                  v-if="isOpen"
                  color="secondary"
                  dense
                  size="14"
                  style="width: 14px"
                >
                  fas fa-caret-down
                </v-icon>
                <v-icon
                  v-else
                  color="secondary"
                  dense
                  size="14"
                  style="width: 14px"
                >
                  fas fa-caret-right
                </v-icon>
                <span
                  class="secondary--text pl-1 text-capitalize"
                >
                  {{ getCategoryLabel(group) }}
                </span>
              </td>
            </template>
            <template #header.assigneeId>
              <StaffSearch
                v-if="!compact"
                v-model.trim="staffFilter"
                :append-icon="staffFilter ? '' : 'fal fa-search'"
                target-class="daily-search-staff py-3"
                :clearable="!!staffFilter"
                dense
                hide-details
                solo
              />
              <portal
                :disabled="compact"
                to="daily-schedule-actions"
              >
                <div class="column-actions d-inline-block">
                  <v-tooltip
                    v-if="$can('edit', 'shift') || $can('edit', 'event')"
                    top
                  >
                    <template #activator="{ on, attrs }">
                      <v-menu
                        min-width="150"
                        offset-y
                      >
                        <template v-slot:activator="{ on: menuOn }">
                          <v-btn
                            :class="actionStyles.addActivity.button.classes"
                            icon
                            value="staff"
                            v-bind="attrs"
                            v-on="{ ...on, ...menuOn }"
                          >
                            <v-icon
                              :class="actionStyles.addActivity.icon.classes"
                              size="16"
                            >
                              fal fa-plus
                            </v-icon>
                          </v-btn>
                        </template>
                        <v-list
                          dense
                          class="py-0"
                        >
                          <v-list-item
                            v-if="$can('edit', 'shift')"
                            @click.prevent="setPanelName('addShift')"
                          >
                            <v-list-item-title class="body-2 font-weight-regular">
                              {{ $tc('labels.addShift', 1) }}
                            </v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            v-if="$can('edit', 'event')"
                            @click.prevent="setPanelName('addEvent')"
                          >
                            <v-list-item-title class="body-2 font-weight-regular">
                              {{ $tc('labels.addEvent', 1) }}
                            </v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>
                    <span class="body-2">
                      {{ $t('labels.addActivity') }}
                    </span>
                  </v-tooltip>
                  <v-tooltip
                    top
                  >
                    <template #activator="{ on, attrs }">
                      <v-badge
                        avatar
                        class="badge-panel"
                        color="transparent"
                        overlap
                      >
                        <template v-slot:badge>
                          <v-icon
                            :class="['status', hasCensusErrors ? '' : 'no-errors']"
                            color="error"
                            size="12"
                          >
                            fas fa-exclamation-triangle
                          </v-icon>
                        </template>
                        <v-btn
                          :class="actionStyles.census.button.classes"
                          icon
                          value="census"
                          v-bind="attrs"
                          v-on="on"
                          @click="setPanelName('census')"
                        >
                          <v-icon
                            :class="actionStyles.census.icon.classes"
                            size="16"
                          >
                            fal fa-cloud-upload-alt
                          </v-icon>
                        </v-btn>
                      </v-badge>
                    </template>
                    <span class="body-2">
                      {{ $t('labels.inputCensus') }}
                    </span>
                  </v-tooltip>
                  <v-tooltip
                    top
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        :class="actionStyles.memos.button.classes"
                        icon
                        value="memos"
                        v-bind="attrs"
                        v-on="on"
                        @click="setPanelName('memos')"
                      >
                        <v-icon
                          :class="actionStyles.memos.icon.classes"
                          size="16"
                        >
                          fal fa-file-alt
                        </v-icon>
                      </v-btn>
                    </template>
                    <span class="body-2">
                      {{ $t('labels.notes') }}
                    </span>
                  </v-tooltip>
                </div>
              </portal>
            </template>
          </v-data-table>
        </v-container>
      </v-container>
    </template>
  </v-container>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { DATE_FORMAT, DATE_FORMAT_US, getAvatar } from '@/utils';
import {
  calculateShiftHours,
  getCensusRatio,
  isProductiveShift,
  isWorkingShiftForDisplay,
  isWorkingShiftForValidation,
  shiftMatchesFilters,
  wasShiftModifiedByManagement
} from '@/utils/scheduling';
import { showStatus } from '@/plugins/vue-notification';
import ScheduleSymbol from '@/views/scheduling/ScheduleSymbol';
import NurseDetails from '@/views/scheduling/panels/NurseDetails';
import Census from '@/views/scheduling/panels/daily_schedule/Census';
import Notes from '@/views/scheduling/panels/daily_schedule/Notes';
import Details from '@/views/scheduling/panels/daily_schedule/Details';
import AddEvent from '@/views/scheduling/panels/daily_schedule/AddEvent';
import AddShift from '@/views/scheduling/panels/daily_schedule/AddShift';
import EventDetails from '@/views/scheduling/panels/EventDetails';
import EventRequest from '@/views/scheduling/requests/EventRequest';
import ShiftRequest from '@/views/scheduling/requests/ShiftRequest';
import SwapRequest from '@/views/scheduling/requests/SwapRequest';
import StaffSearch from '@/components/StaffSearch';
import UserName from '@/components/scheduling/UserName';
import LastShiftBrowser from '@/components/scheduling/LastShiftBrowser';
import * as Sentry from '@sentry/vue';
import { DAILY_SCHEDULE_STATES, SCHEDULE_STATES } from '@/views/scheduling/constants';
import { ACCOUNT_STATE } from '@/services/constants';
import { RequestCanceledError } from '@/services/errors';
import { userMatchesText } from '@/utils/org';

export default {
  components: {
    LastShiftBrowser,
    ScheduleSymbol,
    StaffSearch,
    UserName
  },
  // The daily schedule page owns the side panels, unsaved changes state and open panel.
  inject: ['dailySchedulePage'],
  props: {
    // Shows a narrow column with fewer details, used when several departments are displayed.
    compact: {
      default: false,
      type: Boolean
    },
    dailyScheduleType: {
      required: true,
      type: Object
    },
    date: {
      required: true,
      type: String
    },
    // Data loaded by the caller, used when loadData is false: null while loading, then
    // { schedule, dailySchedule } as retrieveDailyScheduleDataForDepartments resolves them, or { failed: true }.
    // The caller passes the same schedule when only the daily schedule changed (another daily schedule type).
    departmentData: {
      default: null,
      type: Object
    },
    departmentId: {
      required: true,
      type: Number
    },
    filters: {
      default: null,
      type: Object
    },
    // Loads the column's data. Pages showing several departments load them all at once instead and pass the data.
    loadData: {
      default: true,
      type: Boolean
    },
    showSkeletonLoader: {
      default: true,
      type: Boolean
    }
  },
  data () {
    const categoryMapping = {
      onDuty: 'a',
      onCall: 'b',
      sitter: 'c',
      nonDuty: 'e',
      floatOut: 'd'
    };
    const department = this.$store.getters['org/getDepartmentById'](this.departmentId);
    let schedule = department.schedulingPeriods[0];
    const scheduleForDate = _.find(department.schedulingPeriods, (s) => this.date >= s.startOn && this.date <= s.endOn);
    if (scheduleForDate) {
      schedule = scheduleForDate;
    }

    let selectedJobTypeTab = 0;
    const user = this.$store.state.scheduling.dailySchedule.user;
    if (user) {
      // Compact columns have no job type tabs to jump to.
      if (!this.compact) {
        const jobTypes = this.getJobTypes(department);
        const userSelectedJobTypeTab = _.findIndex(jobTypes, (group) => {
          return _.findIndex(group, (job) => job.associatedJobTypes && job.associatedJobTypes.includes(user.jobTypeId)) >= 0;
        });
        if (userSelectedJobTypeTab >= 0) {
          selectedJobTypeTab = userSelectedJobTypeTab;
        }
      }
      this.$store.commit('scheduling/set_daily_schedule_filters', { user: null });
    }
    return {
      ALL_JOB_TYPES_ID: 0,
      ACCOUNT_STATE,
      DATE_FORMAT_US,
      categoryMapping,
      census: [],
      finalizing: false,
      id: 0,
      lastCanceled: {
        loading: false,
        schedule,
        users: {}
      },
      lastFloat: {
        loading: false,
        schedule,
        users: {}
      },
      lastOvertime: {
        loading: false,
        schedule,
        users: {}
      },
      memos: [],
      retrievingSchedule: true, // Flag for paginating through different time period of the schedule.
      savedCensus: [],
      schedule: {
        id: null,
        key: null,
        state: ''
      },
      scheduleSymbols: this.$store.state.org.settings.scheduling.symbols,
      selectedJobTypeTab,
      selectedRows: [],
      showStaffNotes: false,
      staffFilter: '',
      state: 'draft',
      summary: {
        id: null,
        checkedById: null,
        checkedOn: null,
        date: this.date,
        hospitalId: this.$store.state.org.id
      },
      tableHeight: 500
    };
  },

  computed: {
    actionStyles () {
      const defaultButtonClasses = ['grey', 'lighten-2', 'mr-2'];
      const defaultIconClasses = ['grey--text', 'text--darken-3'];
      const styles = {
        addActivity: {
          button: {
            classes: defaultButtonClasses.concat(['add-activity'])
          },
          icon: {
            classes: defaultIconClasses
          }
        },
        census: {
          button: {
            classes: defaultButtonClasses.concat(['census'])
          },
          icon: {
            classes: defaultIconClasses
          }
        },
        memos: {
          button: {
            classes: defaultButtonClasses.concat(['memos'])
          },
          icon: {
            classes: defaultIconClasses
          }
        },
        viewOpenShift: {
          button: {
            classes: defaultButtonClasses.concat(['view-open-shift'])
          },
          icon: {
            classes: defaultIconClasses
          }
        }
      };

      if (this.memos.length > 0 && this.memos[0].notes) {
        styles.memos.button.classes = ['primary', 'lighten-2', 'memos', 'mr-2'];
        styles.memos.icon.classes = ['white--text'];
      }

      let openedPanelName = this.openedPanelName;
      if (['addShift', 'addEvent'].includes(openedPanelName)) {
        openedPanelName = 'addActivity';
      }

      if (openedPanelName && styles[openedPanelName]) {
        styles[openedPanelName].button.classes = ['primary', 'mr-2', openedPanelName];
        styles[openedPanelName].icon.classes = ['white--text'];
      }

      return styles;
    },
    activeDailyScheduleType () {
      return this.dailyScheduleType;
    },
    activeDepartment () {
      return this.department;
    },
    allowsCensus () {
      const predefinedCensus = _.get(this.activeDailyScheduleType, 'settings.census.predefined', []);
      return predefinedCensus.length > 0;
    },
    associatedJobTypes () {
      const jobTypes = this.jobTypes[this.selectedJobTypeTab];
      const associatedJobTypes = [];
      if (jobTypes) {
        for (let i = 0, count = jobTypes.length; i < count; i++) {
          associatedJobTypes.push(...jobTypes[i].associatedJobTypes);
        }
      }
      return associatedJobTypes;
    },
    categories () {
      return [
        {
          expanded: true,
          key: this.categoryMapping.onCall,
          label: this.$t('labels.onCall'),
          match: (item) => isWorkingShiftForDisplay(item.value, this.shiftFlagsById) && item.value.onCall
        },
        {
          expanded: false,
          key: this.categoryMapping.nonDuty,
          label: this.$t('labels.nonDuty'),
          match: (item) => !item.value.available && !item.value.giveaway && !item.value.swapped && !isWorkingShiftForValidation(item.value, this.shiftFlagsById)
        },
        {
          expanded: true,
          key: this.categoryMapping.floatOut,
          label: this.$t('labels.floatOut'),
          match: (item) => {
            const hasFloatFlag = _.intersection(item.value.flags, this.floatFlagIds).length > 0;
            return hasFloatFlag && item.value.departmentId !== this.activeDepartment.id && isWorkingShiftForValidation(item.value, this.shiftFlagsById);
          }
        },
        {
          expanded: true,
          key: this.categoryMapping.sitter,
          label: this.$t('labels.sitter'),
          match: (item) => isWorkingShiftForValidation(item.value, this.shiftFlagsById) && item.value.sitter
        },
        {
          expanded: true,
          key: this.categoryMapping.onDuty,
          label: this.$t('labels.onDuty'),
          match: (item) => isWorkingShiftForValidation(item.value, this.shiftFlagsById) && item.value.departmentId === this.activeDepartment.id
        }
      ];
    },
    canEdit () {
      return this.$can('edit', 'dailySchedule');
    },
    chargeJobType () {
      return _.find(this.jobTypesById, (jt) => _.get(jt, 'settings.isChargeNurse', false));
    },
    chargeShiftTypes () {
      if (this.chargeJobType) {
        return _.get(this.chargeJobType, 'settings.associatedShiftTypes', []);
      }
      return null;
    },
    dateFormatShort () {
      return this.$store.getters['org/getDateFormatShort']();
    },
    dateFormatLong () {
      return this.$store.getters['org/getDateFormatLong']();
    },
    department () {
      return this.$store.getters['org/getDepartmentById'](this.departmentId);
    },
    hasChanges: {
      get () {
        return this.dailySchedulePage.hasChanges;
      },
      set (hasChanges) {
        this.dailySchedulePage.hasChanges = hasChanges;
      }
    },
    openedPanelName: {
      get () {
        const { name, owner } = this.dailySchedulePage.openedPanel;
        return owner === this.departmentId ? name : undefined;
      },
      set (name) {
        this.dailySchedulePage.setOpenedPanel(name, this.departmentId);
      }
    },
    panels () {
      return this.dailySchedulePage.panels;
    },
    filteredItems () {
      const sortedItems = _.sortBy(this.items, [(item) => item.user.fullName]);
      const staffFilter = this.staffFilter ? this.staffFilter.toLowerCase() : '';
      const filteredItems = _.filter(sortedItems, (item) => {
        let matchesStaffFilter = true;
        if (staffFilter) {
          matchesStaffFilter = userMatchesText(item.user, staffFilter);
        }
        return _.indexOf(this.associatedJobTypes, item.user.jobTypeId) >= 0 && matchesStaffFilter && this.matchesFilters(item);
      });

      const items = [];
      const itemsByCategory = {};
      for (let i = 0, count = filteredItems.length; i < count; i++) {
        const item = filteredItems[i];
        if (!itemsByCategory[item.category]) {
          itemsByCategory[item.category] = [];
        }
        itemsByCategory[item.category].push(item);
      }

      for (let categoryKey in itemsByCategory) {
        let activities = itemsByCategory[categoryKey];
        if (this.sort.columns.length > 0 && this.sort.order.length > 0) {
          const column = this.sort.columns[0];
          const iteratees = [
            (a) => {
              if (!a.value) {
                return '';
              }
              switch (column) {
                case 'lastCancel':
                  return _.get(this.lastCanceled.users, [a.value.assigneeId, 'last', 'date'], '');
                case 'lastFloat':
                  return _.get(this.lastFloat.users, [a.value.assigneeId, 'last', 'date'], '');
                case 'lastOvertime':
                  return _.get(this.lastOvertime.users, [a.value.assigneeId, 'last', 'date'], '');
                case 'typeId':
                  if (a.type === 'shift') {
                    return a.value.startTime;
                  } else {
                    return '';
                  }
                default:
                  return '';
              }
            }
          ];
          const orders = [this.sort.order[0] ? 'desc' : 'asc'];
          activities = _.orderBy(activities, iteratees, orders);
        }
        items.push({
          id: _.uniqueId(), // This ID is just for the data table to distinguish rows for selection.
          activities,
          category: categoryKey
        });
      }
      return items;
    },
    floatFlagIds () {
      return _.filter(this.$store.state.org.flags, f => f.float).map(f => f.id);
    },
    shiftFlagsById () {
      return this.$store.state.org.flags.reduce((flags, value) => {
        flags[value.id] = value;
        return flags;
      }, {});
    },
    hasCensusErrors () {
      for (let jobId in this.jobTypeCount) {
        if (this.ratioCount[jobId] && this.ratioCount[jobId] !== this.jobTypeCount[jobId]) {
          return true;
        }
      }
      return false;
    },
    headers () {
      if (this.compact) {
        // Last float/cancel/overtime are shown in the staff details panel instead of an expandable row.
        // The name column is as wide as the name it shows (178px plus padding), so both columns fit the fixed width
        // of the column even with a vertical scrollbar.
        return [
          { sortable: false, text: this.$t('labels.name'), value: 'assigneeId', width: 210 },
          { sortable: true, text: this.$t('labels.shift'), value: 'typeId', width: 54 }
        ];
      }
      return [
        { sortable: false, text: this.$t('labels.name'), value: 'assigneeId', width: 220 },
        { sortable: true, text: this.$t('labels.shift'), value: 'typeId', width: 54 },
        { sortable: false, text: this.$tc('labels.flag', 2), value: 'flags', width: 220 },
        { class: 'shift-column', sortable: false, text: this.$tc('labels.comment', 2), value: 'comments', width: 220 },
        { class: 'shift-column', sortable: true, text: this.$t('labels.lastFloat'), value: 'lastFloat', width: 150 },
        { class: 'shift-column', sortable: true, text: this.$t('labels.lastCancel'), value: 'lastCancel', width: 150 },
        { class: 'shift-column', sortable: true, text: this.$t('labels.lastOvertime'), value: 'lastOvertime', width: 150 },
        { text: '', value: 'data-table-expand' }
      ];
    },
    items () {
      const records = _.get(this.$store.state.scheduling.grids[this.schedule.key], ['records'], []);
      const items = [];
      const field = moment(this.date).valueOf();
      let item;
      for (let r of records) {
        const activities = _.get(r, [field, 'activities'], []);
        const request = _.get(r, [field, 'request'], null);
        for (let i = 0, len = activities.length; i < len; i++) {
          switch (activities[i].type) {
            case 'shift':
              item = this.getShiftItem(activities[i], r.user);
              if (item && this.activeDailyScheduleType.shiftTypes.includes(activities[i].typeId)) {
                items.push({
                  ...item,
                  request
                });
              }
              break;
            case 'event':
              items.push(this.getEventItem(activities[i], r.user));
              break;
          }
        }
      }
      return items;
    },
    jobStatusById () {
      return this.$store.state.org.jobStatus.reduce(
        (obj, jobStatus) => {
          obj[jobStatus.id] = jobStatus;
          return obj;
        }, // eslint-disable-line no-return-assign, no-sequences
        {}
      );
    },
    jobTypeAssignees () {
      const jobTypeAssignees = {
        'sitter': []
      };
      const jobTypeMapping = {};
      for (let group = 0, groupCount = this.jobTypes.length; group < groupCount; group++) {
        for (let i = 0, count = this.jobTypes[group].length; i < count; i++) {
          const jobType = this.jobTypes[group][i];
          jobTypeAssignees[jobType.id] = [];
          const ids = String(jobType.id).split('|');

          for (let j = 0, idsCount = ids.length; j < idsCount; j++) {
            jobTypeMapping[ids[j]] = jobType.id;
          }
        }
      }
      const chargeShiftTypes = this.chargeShiftTypes;
      for (let i = 0, count = this.items.length; i < count; i++) {
        const item = this.items[i];
        if (item.category === this.categoryMapping.onDuty || item.category === this.categoryMapping.sitter) {
          let jobTypeId = item.user.jobTypeId;
          if (item.type === 'shift' && item.value && !isProductiveShift(item.value, this.shiftFlagsById)) {
            continue;
          }
          if (item.type === 'shift' && item.value && item.user.charge && chargeShiftTypes) {
            const associatedShiftTypes = _.get(this.jobTypesById[jobTypeId], 'settings.associatedShiftTypes', []);
            if (chargeShiftTypes.includes(item.value.typeId) && !associatedShiftTypes.includes(item.value.typeId)) {
              jobTypeId = this.chargeJobType.id;
            }
          }
          if (item.value.sitter) {
            jobTypeAssignees['sitter'].push(item.user.userId);
          } else if (jobTypeAssignees[jobTypeMapping[jobTypeId]]) {
            jobTypeAssignees[jobTypeMapping[jobTypeId]].push(item.user.userId);
          } else {
            if (!jobTypeAssignees[jobTypeId]) {
              jobTypeAssignees[jobTypeId] = [];
            }
            jobTypeAssignees[jobTypeId].push(item.user.userId);
          }
        }
      }
      return jobTypeAssignees;
    },
    jobTypeHours () {
      const jobTypeHours = {
        'sitter': 0
      };
      const jobTypeMapping = {};
      for (let group = 0, groupCount = this.jobTypes.length; group < groupCount; group++) {
        for (let i = 0, count = this.jobTypes[group].length; i < count; i++) {
          const jobType = this.jobTypes[group][i];
          jobTypeHours[jobType.id] = 0;
          const ids = String(jobType.id).split('|');

          for (let j = 0, idsCount = ids.length; j < idsCount; j++) {
            jobTypeMapping[ids[j]] = jobType.id;
          }
        }
      }
      const chargeShiftTypes = this.chargeShiftTypes;
      for (let i = 0, count = this.items.length; i < count; i++) {
        const item = this.items[i];
        if (item.category === this.categoryMapping.onDuty || item.category === this.categoryMapping.sitter) {
          let jobTypeId = item.user.jobTypeId;
          if (item.type === 'shift' && item.value && !isProductiveShift(item.value, this.shiftFlagsById)) {
            continue;
          }
          if (item.type === 'shift' && item.value && item.user.charge && chargeShiftTypes) {
            const associatedShiftTypes = _.get(this.jobTypesById[jobTypeId], 'settings.associatedShiftTypes', []);
            if (chargeShiftTypes.includes(item.value.typeId) && !associatedShiftTypes.includes(item.value.typeId)) {
              jobTypeId = this.chargeJobType.id;
            }
          }
          if (item.value.sitter) {
            jobTypeHours['sitter'] += calculateShiftHours(item.value, this.shiftTypesById[item.value.typeId]);
          } else if (jobTypeHours[jobTypeMapping[jobTypeId]]) {
            jobTypeHours[jobTypeMapping[jobTypeId]] += calculateShiftHours(item.value, this.shiftTypesById[item.value.typeId]);
          } else {
            if (!jobTypeHours[jobTypeId]) {
              jobTypeHours[jobTypeId] = 0;
            }
            if (!jobTypeHours[jobTypeMapping[jobTypeId]]) {
              jobTypeHours[jobTypeMapping[jobTypeId]] = 0;
            }
            jobTypeHours[jobTypeId] += calculateShiftHours(item.value, this.shiftTypesById[item.value.typeId]);
            jobTypeHours[jobTypeMapping[jobTypeId]] = calculateShiftHours(item.value, this.shiftTypesById[item.value.typeId]);
          }
        }
      }
      return jobTypeHours;
    },
    jobTypeCount () {
      const jobTypeCount = {};
      let total = 0;
      for (let id in this.jobTypeAssignees) {
        jobTypeCount[id] = this.jobTypeAssignees[id].length;
        total += jobTypeCount[id];
      }
      jobTypeCount[this.ALL_JOB_TYPES_ID] = total;
      return jobTypeCount;
    },
    jobTypes () {
      return this.getJobTypes(this.activeDepartment);
    },
    jobTypesById () {
      return this.$store.state.org.jobTypes.reduce(
        (obj, jobType) => {
          obj[jobType.id] = jobType;
          return obj;
        }, // eslint-disable-line no-return-assign, no-sequences
        {}
      );
    },
    jobTypesUngrouped () {
      const jobTypes = [];
      // Start at 1 to exclude the all tab
      for (let i = 1, count = this.jobTypes.length; i < count; i++) {
        jobTypes.push(...this.jobTypes[i]);
      }
      return jobTypes;
    },
    ratioCount () {
      let ratio = {};
      let latestCensus = null;
      for (let i = this.census.length - 1; i >= 0; i--) {
        const census = this.census[i];
        const acuities = _.keys(census.settings.acuity);
        if (census.settings.showTotalAcuityByClass) {
          if (census.total) {
            latestCensus = census;
          } else {
            for (let a = 0, aCount = acuities.length; a < aCount; a++) {
              if (census[`acuity${acuities[a]}`]) {
                latestCensus = census;
              }
            }
          }
        }
        if (census.censusBySpecialty) {
          const specialties = census.censusBySpecialty;
          for (let s = 0, sCount = specialties.length; s < sCount; s++) {
            if (specialties[s].showAcuitySubtotal && specialties[s].subtotal) {
              latestCensus = census;
            } else if (specialties[s].acuityBreakdown) {
              for (let a = 0, aCount = acuities.length; a < aCount; a++) {
                if (_.get(specialties[s].acuityBreakdown, [acuities[a], 'value'], null)) {
                  latestCensus = census;
                }
              }
            }
          }
        }
        if (census.extraStaff.length > 0) {
          latestCensus = census;
        }
        if (latestCensus) {
          break;
        }
      }
      if (latestCensus) {
        ratio = getCensusRatio(latestCensus, this.jobTypesUngrouped, this.$store.getters['org/getDailyScheduleTypeById'](this.activeDailyScheduleType.id));
        for (let i = 0, count = latestCensus.extraStaff.length; i < count; i++) {
          if (latestCensus.extraStaff[i].count) {
            if (!ratio[latestCensus.extraStaff[i].jobTypeId]) {
              ratio[latestCensus.extraStaff[i].jobTypeId] = 0;
            }
            ratio[latestCensus.extraStaff[i].jobTypeId] += latestCensus.extraStaff[i].count;
          }
        }
      }
      return ratio;
    },
    records: {
      cache: false,
      get: function () {
        return _.get(this.$store.state.scheduling.grids[this.schedule.key], ['records'], []);
      }
    },
    readOnly () {
      // Always allow editing shifts regardless of schedule state and date.
      return !this.$can('edit', 'dailySchedule');
    },
    scheduleStateIndicator () {
      if (!this.schedule.id) {
        return null;
      }
      const mobile = this.$vuetify.breakpoint.lgAndDown;
      const stateIndicator = {
        stateLabelCssClass: 'grey darken-1',
        stateLabelKey: mobile ? 'labels.scheduleStatePastAbbr' : 'labels.scheduleStatePast'
      };
      switch (this.schedule.state) {
        case SCHEDULE_STATES.PENDING_POST_APPROVAL:
        case SCHEDULE_STATES.PENDING_PUBLISH_APPROVAL:
          stateIndicator.stateLabelCssClass = 'error';
          stateIndicator.stateLabelKey = mobile ? 'labels.scheduleStatePendingApprovalDirectorAbbr' : 'labels.scheduleStatePendingApprovalDirector';
          break;

        case SCHEDULE_STATES.UNDER_NURSE_REVIEW:
          stateIndicator.stateLabelCssClass = 'info';
          stateIndicator.stateLabelKey = mobile ? 'labels.scheduleStateUnderReviewNurseAbbr' : 'labels.scheduleStateUnderReviewNurse';
          break;

        case SCHEDULE_STATES.PUBLISHED:
          stateIndicator.stateLabelCssClass = 'success';
          stateIndicator.stateLabelKey = mobile ? 'labels.scheduleStatePublishedAbbr' : 'labels.scheduleStatePublished';
          break;

        case SCHEDULE_STATES.DRAFT:
          stateIndicator.stateLabelCssClass = 'nb-orange';
          stateIndicator.stateLabelKey = mobile ? 'labels.scheduleStateDraftAbbr' : 'labels.scheduleStateDraft';
          break;
        case SCHEDULE_STATES.SELF_SCHEDULE:
          stateIndicator.stateLabelCssClass = 'nb-purple';
          stateIndicator.stateLabelKey = mobile ? 'labels.scheduleStateSelfScheduleAbbr' : 'labels.scheduleStateSelfSchedule';
          break;
        case SCHEDULE_STATES.CURRENT:
          stateIndicator.stateLabelCssClass = 'success';
          stateIndicator.stateLabelKey = mobile ? 'labels.scheduleStateCurrentAbbr' : 'labels.scheduleStateCurrent';
          break;
        case SCHEDULE_STATES.INITIAL:
          stateIndicator.stateLabelCssClass = 'nb-purple';
          stateIndicator.stateLabelKey = mobile ? 'labels.scheduleStateInitialAbbr' : 'labels.scheduleStateInitial';
          break;
      }

      return stateIndicator;
    },
    selectedLastCanceledScheduleText () {
      const startFrom = moment(this.lastCanceled.schedule.startOn);
      const endBy = moment(this.lastCanceled.schedule.endOn);
      let text = '';
      if (endBy.isSame(startFrom, 'year')) {
        text = startFrom.format(this.dateFormatShort) + ' - ' + endBy.format(this.dateFormatLong);
      } else {
        // Display year info for both start and end date if the end date is in different year.
        text = startFrom.format(this.dateFormatLong) + ' - ' + endBy.format(this.dateFormatLong);
      }
      return text;
    },
    selectedLastFloatScheduleText () {
      const startFrom = moment(this.lastFloat.schedule.startOn);
      const endBy = moment(this.lastFloat.schedule.endOn);
      let text = '';
      if (endBy.isSame(startFrom, 'year')) {
        text = startFrom.format(this.dateFormatShort) + ' - ' + endBy.format(this.dateFormatLong);
      } else {
        // Display year info for both start and end date if the end date is in different year.
        text = startFrom.format(this.dateFormatLong) + ' - ' + endBy.format(this.dateFormatLong);
      }
      return text;
    },
    selectedLastOvertimeScheduleText () {
      const startFrom = moment(this.lastOvertime.schedule.startOn);
      const endBy = moment(this.lastOvertime.schedule.endOn);
      let text = '';
      if (endBy.isSame(startFrom, 'year')) {
        text = startFrom.format(this.dateFormatShort) + ' - ' + endBy.format(this.dateFormatLong);
      } else {
        // Display year info for both start and end date if the end date is in different year.
        text = startFrom.format(this.dateFormatLong) + ' - ' + endBy.format(this.dateFormatLong);
      }
      return text;
    },
    shiftTypes () {
      return this.$store.state.org.shiftTypes.reduce(
        (obj, shiftType) => (obj[shiftType.id] = shiftType, obj), // eslint-disable-line no-return-assign, no-sequences
        {}
      );
    },
    shiftTypesById () {
      return this.$store.state.org.shiftTypes.reduce(
        (obj, shiftType) => {
          obj[shiftType.id] = shiftType;
          return obj;
        }, // eslint-disable-line no-return-assign, no-sequences
        {}
      );
    },
    showFinalizeButton () {
      return this.$can('edit', 'dailySchedule') && this.schedule.id && [SCHEDULE_STATES.CURRENT, SCHEDULE_STATES.PUBLISHED].includes(this.schedule.state) && this.state !== DAILY_SCHEDULE_STATES.FINALIZED;
    },
    showFinalizeMessage () {
      return this.$can('edit', 'dailySchedule') && this.schedule.id && [SCHEDULE_STATES.CURRENT, SCHEDULE_STATES.PUBLISHED].includes(this.schedule.state) && this.state === DAILY_SCHEDULE_STATES.FINALIZED;
    },
    sort () {
      return this.$store.state.scheduling.dailySchedule.sort;
    },
    storeSchedule () {
      return this.$store.state.scheduling.schedules[this.schedule.key];
    },
    userRecordMap () {
      return _.get(this.$store.state.scheduling.grids[this.schedule.key], ['userRecordMap'], {});
    }
  },
  watch: {
    dailyScheduleType () {
      if (this.loadData) {
        this.retrieveDailyScheduleRecord();
      }
    },
    date () {
      if (this.loadData) {
        this.retrieveData();
      }
    },
    departmentData: {
      handler (data) {
        if (this.loadData) {
          return;
        }
        if (!data) {
          // The caller is loading the data.
          this.retrievingSchedule = true;
          this.staffFilter = '';
        } else if (data.failed) {
          // The caller reports the failure.
          this.setLoadFailed();
        } else if (data.schedule === this.schedule) {
          this.applyDailySchedule(data.dailySchedule);
        } else {
          this.applyLoadedData(data.schedule, data.dailySchedule);
        }
      },
      immediate: true
    },
    retrievingSchedule () {
      if (!this.retrievingSchedule) {
        this.$nextTick(() => {
          for (let i = 0, count = this.categories.length; i < count; i++) {
            const ref = this.$refs[this.categories[i].key];
            if (ref) {
              const expanded = ref.classList.contains('expanded');
              if (expanded !== this.categories[i].expanded) {
                this.$refs[this.categories[i].key].click();
              }
            }
          }
        });
      }
    },
    'lastCanceled.schedule' () {
      if (!this.retrievingSchedule) {
        this.retrieveCanceledData();
      }
    },
    'lastFloat.schedule' () {
      if (!this.retrievingSchedule) {
        this.retrieveFloatData();
      }
    },
    'lastOvertime.schedule' () {
      if (!this.retrievingSchedule) {
        this.retrieveOvertimeData();
      }
    },
    selectedJobTypeTab () {
      this.refreshPanels();
    }
  },
  created () {
    // Only the latest load is applied; older responses (e.g. after switching dates while loading) are ignored.
    this.loadSequence = 0;
    this.fullLoadInFlight = false;
  },
  mounted () {
    this.$nextTick(function () {
      this.updateDynamicHeights();
    });
    if (this.loadData) {
      this.retrieveData();
    }
    window.addEventListener('resize', this.updateDynamicHeights);
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateDynamicHeights);
  },
  methods: {
    has: _.has,
    closeAllPanels () {
      this.dailySchedulePage.closeAllPanels();
    },
    closeLastPanel () {
      this.dailySchedulePage.closeLastPanel();
    },
    // This function is added mainly for easy of mocking during in unit tests.
    dispatch (action, payload) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch(action, payload).then(response => {
          resolve(response);
        }).catch(error => {
          reject(error);
        });
      });
    },
    expandGroup (group, isOpen, toggle) {
      const category = _.find(this.categories, (category) => category.key === group);
      if (category) {
        category.expanded = !isOpen;
      }
      toggle();
    },
    finalizeScheduleChanges () {
      this.finalizing = true;
      let action = '';
      let payload = {};
      if (!this.id) {
        action = 'scheduling/createDailySchedule';
        payload = {
          date: this.date,
          departmentId: this.activeDepartment.id,
          typeId: this.activeDailyScheduleType.id,
          state: DAILY_SCHEDULE_STATES.FINALIZED
        };
      } else if (this.id > 0) {
        action = 'scheduling/updateDailySchedule';
        payload = {
          id: this.id,
          data: {
            state: DAILY_SCHEDULE_STATES.FINALIZED
          }
        };
      } else {
        this.finalizing = false;
        return;
      }

      this.$dialog.confirm({
        title: this.$t('labels.finalizeCurrentSchedule'),
        subtitle: '',
        bodyIcon: '',
        body: this.$t('descriptions.finalizeCurrentSchedule'),
        confirmText: this.$t('labels.finalize'),
        cancelText: this.$t('labels.cancel')
      }, { persistent: true, width: 400 }).then(() => {
        this.dispatch(action, payload).then((dailySchedule) => {
          this.id = dailySchedule.id;
          this.state = dailySchedule.state;

          showStatus({
            text: this.$t('descriptions.dailyScheduleFinalizedSuccess')
          });
        }).catch(error => {
          const data = {
            error: _.get(error, 'response.data')
          };

          showStatus({
            text: this.$t('descriptions.dailyScheduleFinalizedFail'),
            type: 'error',
            data
          });
        }).finally(() => {
          this.finalizing = false;
        });
      }).catch(() => {
        this.finalizing = false;
      });
    },
    findEventIndexById (id) {
      return _.findIndex(this.items, (item) => {
        return item.type === 'event' && item.value.id === id;
      });
    },
    findShiftById (userId, id) {
      const row = _.get(this.records, [this.userRecordMap[userId]], {});
      for (let prop in row) {
        const timestamp = moment(parseInt(prop));
        if (!timestamp.isValid()) {
          continue;
        }

        const shift = _.find(_.get(row[prop], 'activities', []), (a) => {
          return a.type === 'shift' && a.id === id;
        });
        if (shift) {
          return shift;
        }
      }
      return null;
    },
    /**
     * Finds a shift in this column's grid by ID, whoever it is assigned to.
     * @param {number} id Shift ID
     * @returns {Object|null}
     */
    findShiftInGrid (id) {
      for (const row of this.records) {
        for (const prop in row) {
          if (prop === 'user') {
            continue;
          }
          const shift = _.find(_.get(row[prop], 'activities', []), (a) => a.type === 'shift' && a.id === id);
          if (shift) {
            return shift;
          }
        }
      }
      return null;
    },
    findShiftIndexById (id) {
      return _.findIndex(this.items, (item) => {
        return item.type === 'shift' && item.value.id === id;
      });
    },
    getAvatar,
    getCategoryCount (key, jobTypeIndex) {
      let associatedJobTypes = [];
      if (_.has(this.jobTypes, `${jobTypeIndex}.associatedJobTypes`)) {
        associatedJobTypes = _.get(this.jobTypes, `${jobTypeIndex}.associatedJobTypes`, []);
      }
      const count = this.items.reduce((count, item) => {
        if (item.category === key && associatedJobTypes.includes(item.user.jobTypeId)) {
          count++;
        }
        return count;
      }, 0);

      return count || '';
    },
    getCategoryLabel (key) {
      let label = '';
      const category = _.find(this.categories, (category) => category.key === key);
      if (category) {
        label = category.label;
      }
      return label;
    },
    getDates (shifts) {
      const dates = [];
      for (let i = 0, count = shifts.length; i < count; i++) {
        dates.push(moment(shifts[i].payrollDate || shifts[i].date).format(DATE_FORMAT_US));
      }
      return _.uniq(dates).join(', ');
    },
    getDepartmentByID (id) {
      return this.$store.getters['org/getDepartmentById'](id);
    },
    getEventItem (event, user) {
      return {
        id: `event${event.id}`, // This ID is just for the data table to distinguish rows for selection.
        category: this.categoryMapping.nonDuty,
        value: event,
        jobTypeIndex: this.getJobTypeIndex(user),
        type: 'event',
        user: {
          ...user,
          id: user.userId,
          profileId: user.id
        }
      };
    },
    getEventType (event) {
      const eventType = this.$store.getters['org/getEventTypeById'](event.typeId);
      const type = {};
      if (eventType) {
        type.name = eventType.name;
        type.color = _.get(eventType, 'styles.web.color', '');
      }
      return type;
    },
    getJobCount (jobIndex) {
      let id = null;
      if (_.has(this.jobTypes, `${jobIndex}.id`)) {
        id = _.get(this.jobTypes, `${jobIndex}.id`, null);
      } else {
        id = _.get(this.jobTypesById, [jobIndex, 'id'], null);
      }
      let count = 0;
      if (id) {
        count = this.jobTypeCount[id];
      }
      return count;
    },
    getJobHours (jobIndex) {
      let id = null;
      if (_.has(this.jobTypes, `${jobIndex}.id`)) {
        id = _.get(this.jobTypes, `${jobIndex}.id`, null);
      } else {
        id = _.get(this.jobTypesById, [jobIndex, 'id'], null);
      }
      let hours = 0;
      if (id) {
        hours = this.jobTypeHours[id];
      }
      return hours;
    },
    getJobInfo (itemData) {
      const jobInfo = [itemData.user.jobTypeName, itemData.user.jobStatusShortCode].filter(Boolean).join(' ');
      if (itemData.user.departmentId !== this.activeDepartment.id) {
        return `${jobInfo} | ${itemData.user.departmentName}`;
      } else {
        return jobInfo;
      }
    },
    /**
     * Gets the job info and full flag names of a shift item, e.g. for a tooltip when flags are cut off.
     * @param {Object} item Daily schedule item
     * @returns {string}
     */
    getJobAndFlagsText (item) {
      return [this.getJobInfo(item), this.getShiftFlags(item).map((flag) => flag.name).join(', ')].join(' · ');
    },
    getJobLabel (jobIndex) {
      if (_.has(this.jobTypes, `${jobIndex}.name`)) {
        return _.get(this.jobTypes, `${jobIndex}.name`, '');
      } else {
        return _.get(this.jobTypesById, [jobIndex, 'name'], '');
      }
    },
    getJobTypeIndex (user, shift) {
      let jobTypeId = user.jobTypeId;
      const chargeShiftTypes = this.chargeShiftTypes;
      if (shift && user.charge && chargeShiftTypes) {
        const associatedShiftTypes = _.get(this.jobTypesById[jobTypeId], 'settings.associatedShiftTypes', []);
        if (chargeShiftTypes.includes(shift.typeId) && !associatedShiftTypes.includes(shift.typeId)) {
          jobTypeId = this.chargeJobType.id;
        }
      }

      for (let group = 1, groupCount = this.jobTypes.length; group < groupCount; group++) {
        for (let jobType = 0, jobTypeCount = this.jobTypes[group].length; jobType < jobTypeCount; jobType++) {
          if (this.jobTypes[group][jobType].associatedJobTypes.includes(jobTypeId)) {
            return `${group}.${jobType}`;
          }
        }
      }

      return jobTypeId;
    },
    getJobTypes (department) {
      const staffNeeded = _.cloneDeep(_.get(department, ['settings', 'staffNeeded'], []));
      const jobTypesMap = this.$store.state.org.jobTypes.reduce(function (accumulator, currentValue) {
        accumulator[currentValue.id] = currentValue;
        return accumulator;
      }, {});
      const jobTypes = [];
      for (let i = 0, count = staffNeeded.length; i < count; i++) {
        const associatedJobTypes = [];
        const descriptions = [];
        const names = [];
        let partakeInScheduling = false;
        for (let j = 0, typeCount = staffNeeded[i].jobTypes.length; j < typeCount; j++) {
          const jobTypeInfo = jobTypesMap[staffNeeded[i].jobTypes[j]];
          if (jobTypeInfo) {
            associatedJobTypes.push(jobTypeInfo.id);
            descriptions.push(jobTypeInfo.description);
            names.push(jobTypeInfo.name);
            if (jobTypeInfo.partakeInScheduling) {
              partakeInScheduling = true;
            }
          }
        }
        if (partakeInScheduling) {
          jobTypes.push({
            description: descriptions.join(' / '),
            id: staffNeeded[i].jobTypes.join('|'),
            name: names.join(' / '),
            associatedJobTypes,
            associatedShiftTypes: _.keys(staffNeeded[i].shiftTypes).map(id => parseInt(id)),
            staffNeeded: staffNeeded[i].shiftTypes,
            staffingMatrix: staffNeeded[i].staffingMatrix,
            groupRight: _.get(staffNeeded[i], 'display.dailySchedule.groupRight', false)
          });
        }
      }
      const groupedJobTypes = [];
      let group = [];
      if (jobTypes.length > 0) {
        group.push(jobTypes[0]);
      }
      for (let i = 1, count = jobTypes.length; i < count; i++) {
        if (group[group.length - 1].groupRight) {
          group.push(jobTypes[i]);
        } else {
          groupedJobTypes.push([...group]);
          group = [jobTypes[i]];
        }
      }
      groupedJobTypes.push(group);

      groupedJobTypes.unshift([
        {
          description: this.$t('labels.all'),
          id: this.ALL_JOB_TYPES_ID,
          name: this.$t('labels.all'),
          associatedJobTypes: _.keys(this.jobTypesById).map(id => parseInt(id)),
          associatedShiftTypes: this.dailyScheduleType.shiftTypes,
          groupRight: false
        }
      ]);

      return groupedJobTypes;
    },
    getShiftItem (shift, user) {
      const category = _.find(this.categories, (category) => category.match({ value: shift }));
      if (!category) {
        return null;
      }
      return {
        id: `shift${shift.id}`, // This ID is just for the data table to distinguish rows for selection.
        category: category.key,
        value: shift,
        jobTypeIndex: this.getJobTypeIndex(user, shift),
        type: 'shift',
        user: {
          ...user,
          id: user.userId,
          profileId: user.id
        }
      };
    },
    getSitterInfo (item) {
      const sitter = {
        room: '',
        reason: ''
      };

      const room = _.get(item, 'settings.sitter.room', null);
      const reason = _.get(item, 'settings.sitter.reason', null);

      if (room) {
        sitter.room = room;
      }
      if (reason) {
        sitter.reason = reason;
      }
      return sitter;
    },
    getSymbol (type, item) {
      let symbol = {};
      switch (type) {
        case 'shift':
          if (item.value.available) {
            symbol = _.cloneDeep(this.scheduleSymbols.available) || {};
          } else {
            symbol = _.cloneDeep(this.shiftTypes[item.value.typeId].styles.web);
          }
          if (item.value.obligatory) {
            if (_.get(symbol, 'web.symbolType', '') === 'text') {
              symbol.web.symbolValue += '*';
            }
          }
          break;
      }
      return symbol;
    },
    /**
     * Gets the flags of a shift item.
     * @param {Object} item Daily schedule item
     * @returns {Array} Flags, skipping ones that no longer exist
     */
    getShiftFlags (item) {
      if (!item.value || item.type !== 'shift') {
        return [];
      }
      return _.filter(_.map(item.value.flags, (flagId) => this.shiftFlagsById[flagId]));
    },
    getShiftTime (shift) {
      const shiftType = this.shiftTypes[shift.typeId];
      const startTime = shift.startTime ? shift.startTime : shiftType.startTime;
      const endTime = shift.endTime ? shift.endTime : shiftType.endTime;
      return [startTime.substring(0, 5), endTime.substring(0, 5)];
    },
    getErrors (user, hypotheticalShift) {
      const errors = [];
      const validators = this.$store.getters['scheduling/getValidator'](this.schedule.key);
      for (let name in validators) {
        const dayErrors = validators[name].getDayErrors(this.date, user, hypotheticalShift);
        if (dayErrors) {
          errors.push(dayErrors);
        }
      }
      return errors;
    },
    hasDefaultShiftTime (shift) {
      const shiftType = this.shiftTypes[shift.typeId];
      let startTime = shift.startTime ? shift.startTime : shiftType.startTime;
      let endTime = shift.endTime ? shift.endTime : shiftType.endTime;
      return startTime === shiftType.startTime && endTime === shiftType.endTime;
    },
    hasDifferentPayrollDate (item) {
      let hasDifferentPayrollDate = false;
      if (item.value.payrollDate) {
        hasDifferentPayrollDate = !moment(item.value.payrollDate).isSame(moment(item.value.date));
      }

      return hasDifferentPayrollDate;
    },
    /**
     * Applies the daily schedule (census, notes, state) of the current daily schedule type. Shifts are the same for
     * every daily schedule type, but the users shown can differ, so the last shifts of new users are loaded.
     * @param {Object} dailySchedule Daily schedule, or null when there is none yet
     */
    applyDailySchedule (dailySchedule) {
      if (dailySchedule) {
        this.id = dailySchedule.id;
        this.updateData(dailySchedule);
      } else {
        this.updateData({
          id: 0,
          census: [],
          memos: [],
          state: 'draft'
        });
      }

      let lastCanceled = [];
      let lastFloat = [];
      let lastOvertime = [];
      for (let item of this.items) {
        if (!this.lastCanceled.users[item.user.id]) {
          this.lastCanceled.users[item.user.id] = {
            last: null
          };
          lastCanceled.push(item.user.id);
        }
        if (!this.lastFloat.users[item.user.id]) {
          this.lastFloat.users[item.user.id] = {
            last: null
          };
          lastFloat.push(item.user.id);
        }
        if (!this.lastOvertime.users[item.user.id]) {
          this.lastOvertime.users[item.user.id] = {
            last: null
          };
          lastOvertime.push(item.user.id);
        }
      }
      lastCanceled = _.uniq(lastCanceled);
      lastFloat = _.uniq(lastFloat);
      lastOvertime = _.uniq(lastOvertime);

      const requests = [];
      if (lastCanceled.length > 0) {
        requests.push(this.retrieveCanceledData(lastCanceled));
      }
      if (lastFloat.length > 0) {
        requests.push(this.retrieveFloatData(lastFloat));
      }
      if (lastOvertime.length > 0) {
        requests.push(this.retrieveOvertimeData(lastOvertime));
      }

      if (requests.length > 0) {
        Promise.all(requests).catch(() => {});
      }

      this.retrievingSchedule = false;
    },
    /**
     * Applies loaded data: the department's schedule, whose shifts are in the store, and its daily schedule.
     * @param {Object} schedule Schedule id, key (where it is kept in the store) and state
     * @param {Object} dailySchedule Daily schedule, or null when there is none yet
     */
    applyLoadedData (schedule, dailySchedule) {
      if (dailySchedule) {
        this.id = dailySchedule.id;
        this.updateData(dailySchedule);
      } else {
        this.updateData({
          id: 0,
          census: [],
          memos: [],
          state: 'draft'
        });
      }

      this.schedule = schedule;
      this.lastCanceled.users = {};
      this.lastFloat.users = {};
      this.lastOvertime.users = {};

      for (let item of this.items) {
        this.lastCanceled.users[item.user.id] = {
          last: null
        };
        this.lastFloat.users[item.user.id] = {
          last: null
        };
        this.lastOvertime.users[item.user.id] = {
          last: null
        };
      }

      Promise.all([
        this.retrieveCanceledData(),
        this.retrieveFloatData(),
        this.retrieveOvertimeData()
      ]).catch(() => {});

      this.retrievingSchedule = false;
      if (!this.compact) {
        // With several departments shown, the active schedule is set when a request is opened instead.
        this.$store.commit('scheduling/set_active_schedule_id', schedule.id);
      }
    },
    /**
     * Applies a shift deleted elsewhere (live update) to this column.
     * @param {number} id Shift ID
     */
    applyShiftDelete (id) {
      const held = this.findShiftInGrid(id);
      if (held) {
        this.$store.commit('scheduling/remove_shift_from_grid', { scheduleId: this.schedule.key, shift: held });
      }
    },
    /**
     * Applies a created or updated shift (live update) to this column: removes the copy this column holds when the
     * shift no longer belongs here (e.g. moved to another department) and adds the shift when it does.
     * @param {Object} shift Latest version of the shift
     */
    applyShiftUpdate (shift) {
      if (!this.storeSchedule) {
        return;
      }
      const date = moment(shift.payrollDate || shift.date).format(DATE_FORMAT);
      const held = this.findShiftInGrid(shift.id);
      if (held) {
        this.$store.commit('scheduling/remove_shift_from_grid', { scheduleId: this.schedule.key, shift: held });
      }
      if (this.belongsInColumn(shift) && this.coversDate(date)) {
        this.$store.commit('scheduling/add_shift_to_grid', { scheduleId: this.schedule.key, shift: { ...shift, type: 'shift' } });
        if (!held) {
          this.onShiftCreated(shift, date);
        }
      }
    },
    /**
     * Checks if a shift belongs in this column. Only the shift's department counts, not the float flag.
     * @param {Object} shift Shift
     * @returns {Boolean}
     */
    belongsInColumn (shift) {
      return shift.departmentId === this.departmentId;
    },
    /**
     * Checks if a date is within the shifts loaded for this department.
     * @param {string} date Date in YYYY-MM-DD format
     * @returns {Boolean}
     */
    coversDate (date) {
      return !!this.storeSchedule && date >= this.storeSchedule.startOn && date <= this.storeSchedule.endOn;
    },
    /**
     * Refreshes the last canceled/float/overtime info after a shift was created through a live update.
     * The daily schedule page adds the shift to the store.
     * @param {Object} shift Created shift
     * @param {string} date Shift date in YYYY-MM-DD format
     */
    onShiftCreated (shift, date) {
      if (date !== this.date) {
        return;
      }
      const category = _.find(this.categories, (category) => category.match({ value: shift }));
      if (category) {
        this.lastCanceled.users[shift.assigneeId] = {
          last: null
        };
        this.lastFloat.users[shift.assigneeId] = {
          last: null
        };
        this.lastOvertime.users[shift.assigneeId] = {
          last: null
        };

        Promise.all([
          this.retrieveCanceledData([shift.assigneeId]),
          this.retrieveFloatData([shift.assigneeId]),
          this.retrieveOvertimeData([shift.assigneeId])
        ]).catch(() => {});
      }
    },
    moment,
    /**
     * Checks if a load was replaced by a newer one, or the column is gone.
     * @param {number} load Load sequence number
     * @returns {Boolean}
     */
    isStaleLoad (load) {
      return this._isDestroyed || load !== this.loadSequence;
    },
    /**
     * Shows that the data couldn't be loaded.
     */
    setLoadFailed () {
      // Setting the ID to -1 indicates an error occurred retrieving daily schedule date.
      this.updateData({
        id: -1,
        census: [],
        memos: [],
        state: 'draft'
      });
      this.retrievingSchedule = false;
    },
    /**
     * Handles a failed load of the latest data.
     * @param {Error} error Error
     */
    onLoadFailed (error) {
      this.setLoadFailed();
      if (error instanceof RequestCanceledError) {
        // Not a failure: the same request is already pending.
        return;
      }
      Sentry.captureException(error);
      showStatus({
        text: this.$t('descriptions.scheduleRetrievalFail'),
        type: 'error',
        data: {
          error: _.get(error, 'response.data')
        }
      });
    },
    /**
     * Checks an item against the filters from the page's filters panel.
     * @param {Object} item Daily schedule item (shift or event)
     * @returns {Boolean}
     */
    matchesFilters (item) {
      if (!this.filters) {
        return true;
      }
      const status = _.get(this.filters, 'user.status', []);
      if (status.length > 0 && !status.includes(item.user.jobStatusId)) {
        return false;
      }
      const shiftTypes = _.get(this.filters, 'user.shiftTypes', []);
      if (shiftTypes.length > 0 && !shiftTypes.includes(item.user.shiftTypeId)) {
        return false;
      }
      switch (item.type) {
        case 'shift':
          return _.get(this.filters, 'shift.enabled', true) && !!shiftMatchesFilters(item.value, this.filters, this.$store);
        case 'event': {
          const eventTypes = _.get(this.filters, 'event.types', []);
          return _.get(this.filters, 'event.enabled', true) && (eventTypes.length === 0 || eventTypes.includes(item.value.typeId));
        }
      }
      return true;
    },
    /**
     * Opens one of this column's panels. Called by the daily schedule page.
     * @param {string} name Panel name
     */
    openPanel (name) {
      switch (name) {
        case 'addEvent':
          this.openCreateEventPanel();
          break;
        case 'addShift':
          this.openCreateShiftPanel();
          break;
        case 'census':
          this.openCensusPanel();
          break;
        case 'memos':
          this.openNotesPanel();
          break;
      }
    },
    openCensusPanel () {
      this.showPanel(() => {
        return {
          component: Census,
          props: {
            id: this.id,
            census: this.census,
            dailyScheduleTypeId: this.activeDailyScheduleType.id,
            date: this.date,
            departmentId: this.activeDepartment.id,
            jobTypeCount: this.jobTypeAssignees,
            jobTypes: this.jobTypesUngrouped,
            readOnly: this.readOnly
          },
          events: {
            close: () => {
              this.openedPanelName = undefined;
              return true;
            },
            change: (dailySchedule) => {
              this.updateData(dailySchedule);
              this.refreshPanels();
            }
          }
        };
      }, [], true);
    },
    openNotesPanel () {
      this.showPanel(() => {
        return {
          component: Notes,
          props: {
            dailySchedule: {
              id: this.id,
              census: this.census,
              memos: this.memos,
              state: this.state
            },
            date: this.date,
            departmentId: this.activeDepartment.id,
            readOnly: this.readOnly,
            typeId: this.activeDailyScheduleType.id
          },
          events: {
            close: () => {
              this.openedPanelName = undefined;
              return true;
            },
            change: (dailySchedule) => {
              this.updateData(dailySchedule);
              this.refreshPanels();
            }
          }
        };
      }, [], true);
    },
    openCreateEventPanel () {
      this.showPanel(() => {
        return {
          component: AddEvent,
          props: {
            date: this.date,
            departmentId: this.department.id
          },
          events: {
            close: () => {
              this.openedPanelName = undefined;
              return true;
            },
            created: (event) => {
              const employee = this.$store.state.org.employees[event.assigneeId];
              this.showItemDetails(this.getEventItem(event, employee), false);
            }
          }
        };
      }, [], true);
    },
    openCreateShiftPanel () {
      this.showPanel(() => {
        return {
          component: AddShift,
          props: {
            date: this.date,
            departmentId: this.department.id,
            selectedStaff: {}
          },
          events: {
            close: () => {
              this.openedPanelName = undefined;
              return true;
            },
            'add-staff': (data) => {
              const { departmentId, employee, shift, section } = data;
              let changes = {};
              let value = {};
              if (shift && ['floatOutData', 'floatInData'].includes(section)) {
                changes = {
                  canceled: false,
                  departmentId
                };
                value = _.cloneDeep(shift);
                if (!this.activeDailyScheduleType.shiftTypes.includes(value.typeId)) {
                  value.typeId = null;
                  value.startTime = null;
                  value.endTime = null;
                }
              } else {
                let shiftTemplate = {
                  available: false,
                  id: null,
                  comments: '',
                  endTime: '',
                  flags: [],
                  internalComments: '',
                  obligatory: false,
                  onCall: false,
                  settings: {},
                  startTime: '',
                  typeId: null
                };
                if (shift && shift.available) {
                  shiftTemplate = shift;
                }
                _.set(shiftTemplate, 'settings.sitter', {
                  reason: '',
                  room: ''
                });
                value = {
                  ...shiftTemplate,
                  assigneeId: employee.userId,
                  canceled: false,
                  date: this.date,
                  departmentId: departmentId,
                  sitter: false
                };
              }
              this.showItemDetails({
                action: 'add',
                id: _.uniqueId(),
                category: null,
                changes,
                jobTypeIndex: this.getJobTypeIndex(employee, value),
                value,
                type: 'shift',
                user: {
                  ...employee,
                  departmentId: employee.departmentId,
                  id: employee.userId,
                  fullName: employee.fullName,
                  profileId: employee.id
                }
              }, false);
            }
          }
        };
      }, [], true);
    },
    refreshPanels (count) {
      this.dailySchedulePage.refreshPanels(count);
    },
    retrieveData (reset = true) {
      const load = ++this.loadSequence;
      this.fullLoadInFlight = true;
      if (reset) {
        this.retrievingSchedule = true;
        this.closeAllPanels();
        this.staffFilter = '';
      }
      const dailySchedulecriteria = {
        deptId: this.activeDepartment.id,
        date: this.date,
        placeholderId: `daily-schedule-${this.activeDepartment.id}`,
        // Tags the requests so they aren't cancelled as duplicates of an older load's.
        requestTag: `${this.activeDepartment.id}-${load}`,
        shiftTypes: this.activeDailyScheduleType.shiftTypes,
        typeId: this.activeDailyScheduleType.id
      };

      const requests = [
        this.dispatch('scheduling/retrieveDailyScheduleData', {
          ...dailySchedulecriteria,
          // Keeps an older load from replacing this one's data in the store.
          isStale: () => this.isStaleLoad(load)
        })
      ];
      if (this.canEdit) {
        requests.push(this.dispatch('scheduling/retrieveDailySchedule', dailySchedulecriteria));
      }

      return Promise.all(requests).then(response => {
        if (this.isStaleLoad(load)) {
          return;
        }
        this.fullLoadInFlight = false;
        const { schedule } = response[0];
        this.applyLoadedData(schedule, this.canEdit ? response[1] : null);
      }).catch(error => {
        if (this.isStaleLoad(load)) {
          return;
        }
        this.fullLoadInFlight = false;
        this.onLoadFailed(error);
      });
    },
    retrieveCanceledData (users) {
      if (this.compact) {
        // Compact columns show these in the staff details panel instead, which loads its own.
        return Promise.resolve();
      }
      let assignees = _.keys(this.lastCanceled.users);
      if (users) {
        assignees = users;
      }
      if (assignees.length === 0) {
        return Promise.resolve();
      }
      this.lastCanceled.loading = true;
      const lastCanceledCriteria = {
        type: 'canceled',
        before: this.date,
        assignee_ids: assignees.join(',')
      };

      return Promise.all([
        this.dispatch('scheduling/retrieveLastShifts', lastCanceledCriteria)
      ]).then((shifts) => {
        const lastCanceled = shifts[0];
        for (let userId in lastCanceled) {
          this.lastCanceled.users[userId].last = lastCanceled[userId].last;
        }
      }).catch(error => {
        Sentry.captureException(error);
      }).finally(() => {
        this.lastCanceled.loading = false;
      });
    },
    retrieveFloatData (users) {
      if (this.compact) {
        // Compact columns show these in the staff details panel instead, which loads its own.
        return Promise.resolve();
      }
      let assignees = _.keys(this.lastFloat.users);
      if (users) {
        assignees = users;
      }
      if (assignees.length === 0) {
        return Promise.resolve();
      }
      this.lastFloat.loading = true;
      const lastFloatCriteria = {
        type: 'float',
        before: this.date,
        assignee_ids: assignees.join(',')
      };

      return Promise.all([
        this.dispatch('scheduling/retrieveLastShifts', lastFloatCriteria)
      ]).then((shifts) => {
        const lastFloat = shifts[0];
        for (let userId in lastFloat) {
          this.lastFloat.users[userId].last = lastFloat[userId].last;
        }
      }).catch(error => {
        Sentry.captureException(error);
      }).finally(() => {
        this.lastFloat.loading = false;
      });
    },
    retrieveOvertimeData (users) {
      if (this.compact) {
        // Compact columns show these in the staff details panel instead, which loads its own.
        return Promise.resolve();
      }
      let assignees = _.keys(this.lastOvertime.users);
      if (users) {
        assignees = users;
      }
      if (assignees.length === 0) {
        return Promise.resolve();
      }
      this.lastOvertime.loading = true;
      const lastOvertimeCriteria = {
        type: 'overtime',
        before: this.date,
        assignee_ids: assignees.join(',')
      };

      return Promise.all([
        this.dispatch('scheduling/retrieveLastShifts', lastOvertimeCriteria)
      ]).then((shifts) => {
        const lastOvertime = shifts[0];
        for (let userId in lastOvertime) {
          this.lastOvertime.users[userId].last = lastOvertime[userId].last;
        }
      }).catch(error => {
        Sentry.captureException(error);
      }).finally(() => {
        this.lastOvertime.loading = false;
      });
    },
    /**
     * Retrieves the daily schedule (census, notes, state) for the current daily schedule type.
     * Shifts don't need to be reloaded since they were retrieved for all shift types.
     */
    retrieveDailyScheduleRecord () {
      if (this.fullLoadInFlight) {
        // Shifts are still loading, so load everything again for the new daily schedule type.
        this.retrieveData();
        return;
      }
      if (this.canEdit) {
        const load = ++this.loadSequence;
        this.retrievingSchedule = true;
        this.closeAllPanels();
        this.staffFilter = '';
        const dailySchedulecriteria = {
          deptId: this.activeDepartment.id,
          date: this.date,
          requestTag: `${this.activeDepartment.id}-${load}`,
          shiftTypes: this.activeDailyScheduleType.shiftTypes,
          typeId: this.activeDailyScheduleType.id
        };
        this.dispatch('scheduling/retrieveDailySchedule', dailySchedulecriteria).then((dailySchedule) => {
          if (this.isStaleLoad(load)) {
            return;
          }
          this.applyDailySchedule(dailySchedule);
        }).catch(error => {
          if (this.isStaleLoad(load)) {
            return;
          }
          this.onLoadFailed(error);
        });
      }
    },
    setPanelName (panel) {
      this.dailySchedulePage.setPanelName(panel, this.departmentId);
    },
    setSelectedLastCanceledSchedule (schedule, item) {
      this.lastCanceled.schedule = schedule;
      if (this.$refs[`lastCanceled${item.id}`] && this.$refs[`lastCanceled${item.id}`].save) {
        this.$refs[`lastCanceled${item.id}`].save();
      }
    },
    setSelectedLastFloatSchedule (schedule, item) {
      this.lastFloat.schedule = schedule;
      if (this.$refs[`lastFloat${item.id}`] && this.$refs[`lastFloat${item.id}`].save) {
        this.$refs[`lastFloat${item.id}`].save();
      }
    },
    setSelectedLastOvertimeSchedule (schedule, item) {
      this.lastOvertime.schedule = schedule;
      if (this.$refs[`lastOvertime${item.id}`] && this.$refs[`lastOvertime${item.id}`].save) {
        this.$refs[`lastOvertime${item.id}`].save();
      }
    },
    setSortDirection (order) {
      this.$store.commit('scheduling/set_daily_schedule_sort', {
        name: 'order',
        userId: this.$store.state.account.userId,
        value: order
      });
    },
    setSortBy (columns) {
      this.$store.commit('scheduling/set_daily_schedule_sort', {
        name: 'columns',
        userId: this.$store.state.account.userId,
        value: columns
      });
    },
    /**
     * Compact columns have no flags column, so flags show under the job info instead.
     * @param {Object} item Daily schedule item
     * @returns {Boolean}
     */
    showFlagChips (item) {
      return this.compact && this.getShiftFlags(item).length > 0;
    },
    showItemDetails (itemData, reset = true, replace = false) {
      this.selectedRows = [itemData];
      for (const column of this.dailySchedulePage.getColumns()) {
        if (column !== this) {
          column.selectedRows = [];
        }
      }
      if (reset) {
        this.openedPanelName = undefined;
      }
      this.$nextTick(() => {
        if (itemData.request) {
          const request = itemData.request;
          let component;
          switch (request.type) {
            case 'swap':
              component = SwapRequest;
              break;
            case 'event':
              component = EventRequest;
              break;
            case 'shift':
              component = ShiftRequest;
              break;
          }
          if (component) {
            // Approving or rejecting a request updates the grid of the active schedule.
            this.dailySchedulePage.setActiveScheduleId(this.schedule.id);
            this.showPanel(() => {
              return {
                component,
                props: {
                  'request-id': request.id
                },
                events: {
                  'approved': () => {
                    this.closeAllPanels();
                  },
                  'rejected': () => {
                    this.closeAllPanels();
                  }
                }
              };
            }, [], true);
          }
        } else {
          const callback = (itemData) => {
            if (itemData.type === 'shift') {
              let shiftData = itemData;
              if (itemData.value.id && itemData.action !== 'add') {
                const shift = this.findShiftById(shiftData.user.userId, itemData.value.id);
                if (!shift) {
                  this.closeAllPanels();
                  return;
                }
                shiftData = this.getShiftItem(shift, shiftData.user);
                if (!shiftData) {
                  this.closeAllPanels();
                  return;
                }
              }
              return {
                component: Details,
                props: {
                  allowDelete: this.$can('edit', 'shift'),
                  date: moment(this.date),
                  details: shiftData,
                  readOnly: !this.$can('edit', 'shift'),
                  user: this.$store.state.org.employees[shiftData.user.userId],
                  getErrors: this.getErrors
                },
                events: {
                  'saved-splits': ({ createdShifts, updatedShift }) => {
                    for (let i = 0, count = createdShifts.length; i < count; i++) {
                      let needsData = false;
                      if (!this.lastCanceled.users[createdShifts[i].assigneeId]) {
                        needsData = true;
                        this.lastCanceled.users[createdShifts[i].assigneeId] = {
                          last: null
                        };
                      }
                      if (!this.lastFloat.users[createdShifts[i].assigneeId]) {
                        needsData = true;
                        this.lastFloat.users[createdShifts[i].assigneeId] = {
                          last: null
                        };
                      }
                      if (!this.lastOvertime.users[createdShifts[i].assigneeId]) {
                        needsData = true;
                        this.lastOvertime.users[createdShifts[i].assigneeId] = {
                          last: null
                        };
                      }
                      if (needsData) {
                        this.retrieveCanceledData([createdShifts[i].assigneeId]);
                        this.retrieveFloatData([createdShifts[i].assigneeId]);
                        this.retrieveOvertimeData([createdShifts[i].assigneeId]);
                      }
                    }
                    this.$store.commit('scheduling/update_shift', { originalShift: updatedShift, shift: updatedShift });
                    for (let i = 0, len = createdShifts.length; i < len; i++) {
                      this.$store.commit('scheduling/add_shift', createdShifts[i]);
                    }
                    this.refreshPanels();
                  },
                  'canceled-create': () => {
                    this.closeLastPanel();
                  },
                  created: (shift) => {
                    let needsData = false;
                    if (!this.lastCanceled.users[shift.assigneeId]) {
                      needsData = true;
                      this.lastCanceled.users[shift.assigneeId] = {
                        last: null
                      };
                    }
                    if (!this.lastFloat.users[shift.assigneeId]) {
                      needsData = true;
                      this.lastFloat.users[shift.assigneeId] = {
                        last: null
                      };
                    }
                    if (!this.lastOvertime.users[shift.assigneeId]) {
                      needsData = true;
                      this.lastOvertime.users[shift.assigneeId] = {
                        last: null
                      };
                    }
                    if (needsData) {
                      this.retrieveCanceledData([shift.assigneeId]);
                      this.retrieveFloatData([shift.assigneeId]);
                      this.retrieveOvertimeData([shift.assigneeId]);
                    }
                    this.$store.commit('scheduling/add_shift', shift);
                    const idx = this.findShiftIndexById(shift.id);
                    if (idx >= 0) {
                      this.showItemDetails(this.items[idx], reset, true);
                    }
                  },
                  'has-changes': (hasChanges) => {
                    this.hasChanges = hasChanges;
                  },
                  updated: (data) => {
                    this.$store.commit('scheduling/update_shift', data);
                  },
                  removed: (shift) => {
                    this.$store.commit('scheduling/remove_shift', shift);
                    this.closeLastPanel();
                    this.refreshPanels();
                  }
                }
              };
            } else {
              return {
                component: EventDetails,
                props: {
                  allowDelete: this.$can('edit', 'event'),
                  date: moment(this.date),
                  event: itemData.value,
                  readOnly: !this.$can('edit', 'event'),
                  user: this.$store.state.org.employees[itemData.user.userId]
                },
                events: {
                  updated: (data) => {
                    this.$store.commit('scheduling/update_event', data);
                  },
                  removed: (event) => {
                    this.$store.commit('scheduling/remove_event', {
                      event
                    });
                    this.closeLastPanel();
                  }
                }
              };
            }
          };
          if (replace && this.panels.length > 0) {
            this.updatePanel(this.panels.length - 1, callback, [itemData]);
          } else {
            this.showPanel(callback, [itemData], reset);
          }
        }
      });
    },
    showPanel (panelDataCallback, params, resetPanels) {
      this.dailySchedulePage.showPanel(panelDataCallback, params, resetPanels);
    },
    updateNurseDetails (user) {
      this.$store.commit('scheduling/update_user', user);
      this.refreshPanels(this.panels.length - 1);
    },
    toggleNurseDetailsPanel (item) {
      if (this.openedPanelName === 'nurse-details' && this.panels[0].userId === item.user.userId) {
        this.closeAllPanels();
        return;
      }
      this.openedPanelName = 'nurse-details';
      this.showPanel(() => {
        const latestUserInfo = _.cloneDeep(this.$store.state.org.employees[item.user.userId]);
        return {
          component: NurseDetails,
          props: {
            date: this.date,
            departmentId: this.department.id,
            showLastShifts: true,
            user: latestUserInfo
          },
          events: {
            'close': () => {
              this.openedPanelName = undefined;
              return true;
            },
            'notes-saved': (notes) => {
              // Nothing to do
            },
            'updated': (user) => {
              this.$store.commit('scheduling/update_user', user);
              this.refreshPanels(this.panels.length - 1);
            }
          },
          userId: item.user.userId
        };
      }, [], true);
    },
    updateData (dailySchedule) {
      const props = ['id', 'census', 'memos', 'state'];
      for (let prop of props) {
        if (_.has(dailySchedule, [prop])) {
          this[prop] = dailySchedule[prop];
        }
      }
    },
    updateDynamicHeights () {
      this.tableHeight = Math.max(500, window.innerHeight - 215);
    },
    updatePanel (panelIdx, panelDataCallback, params) {
      this.dailySchedulePage.updatePanel(panelIdx, panelDataCallback, params);
    },
    wasShiftModifiedByManagement
  }
};
</script>

<style lang="scss">
.container.department-daily-schedule {
  height: 100%;
  margin-left: 0px;
  vertical-align: top;

  &.compact {
    border: 1px solid map-get($grey, 'lighten-2');
    margin-right: 8px;
    width: 290px;

    .column-actions {
      padding: 8px 0;
    }
    .flag-chips .v-chip {
      vertical-align: middle;
    }
    // No avatar in compact columns, so the name gets its space.
    .categories .schedule-items td .name-n-avatar {
      width: 178px;
    }
    // A fixed width so many flags are cut off with an ellipsis instead of widening the column.
    .categories .schedule-items td .job-info {
      width: 178px;
    }
    // The left border marks expandable rows, which compact columns don't have.
    .categories .schedule-items tr {
      &.shift, &.event {
        td:first-child {
          border-left: none;
        }
      }
    }
    .column-header {
      background-color: white;
      min-height: 40px;
    }
    .department-name {
      min-width: 0px;
    }
    // The column has a fixed width, so its tables never scroll sideways: each category's table gets the column's
    // full width, and the overlay starts after the narrower name column.
    .categories td.category-cell {
      padding: 0;
    }
    .categories .read-only-overlay {
      left: 210px;
    }
    .v-data-table__wrapper {
      overflow-x: hidden;
    }
  }
  &.full {
    width: 100%;
  }

  .actions, .column-actions {
    .v-btn {
      border-radius: 18px !important;
      height: 36px !important;
      min-width: 36px !important;
      width: 36px !important;

      &::before {
        opacity: 0 !important;
      }
    }
  }

  .attendance-chip {
    display: inline-block;
    text-align: center;
    width: 80px;
  }
  .body-container {
    background-color: white;
    border-color: map-get($grey, 'lighten-3');
    border-style: solid;
    border-width: 1px;

    &.actions {
      border-bottom: none;
    }
  }
  .categories {
    .shift-column {
      padding-left: 26px;
    }
    tr {
      th:nth-child(2) {
        padding: 0px;
        text-align-last: center;
      }
      td:nth-child(2) {
        background-color: #F4F4F4;
        padding: 0px;
      }
    }
    td {
      position: relative;
    }
    .read-only-overlay {
      background: rgba(224, 224, 224, 0.3);
      height: 100%;
      left: 220px;
      pointer-events: none;
      position: absolute;
      // Up to the table's edge; a width would reach past it and make the table scroll sideways.
      right: 0px;
      top: 0px;
    }
    .schedule-items {
      border-top: 1px solid map-get($grey, 'lighten-3');
      &.single {
        .v-row-group__header {
          visibility: collapse;
        }
      }
      .management {
        font-size: 8px !important;
        position: absolute;
        top: 4px;
        right: 4px;
      }
      .payroll-diff {
        font-size: 8px !important;
        position: absolute;
        top: 4px;
        left: 4px;
      }
      .request {
        font-size: 8px !important;
        position: absolute;
        top: 4px;
        right: 24px;
      }
      .non-duty {
        border-top: 1px solid map-get($grey, 'darken-3');
        display: inline-block;
        left: 12px;
        position: absolute;
        top: 10px;
        -webkit-transform: rotate(20deg);
        -moz-transform: rotate(20deg);
        -ms-transform: rotate(20deg);
        -o-transform: rotate(20deg);
        transform: rotate(20deg);
        width: 30px;

        &.custom-time {
          left: 2px;
          top: 18px;
          -webkit-transform: rotate(42deg);
          -moz-transform: rotate(42deg);
          -ms-transform: rotate(42deg);
          -o-transform: rotate(42deg);
          transform: rotate(42deg);
          width: 50px;
        }
      }
      .shift-type {
        &.modified {
          span {
            font-weight: bold !important;
          }
        }
      }
      thead {
        visibility: collapse;
      }
      tr {
        &.shift, &.event {
          td:first-child {
            border-left: 2px solid $secondary;
          }
        }
        &.v-data-table__expanded__content {
          background-color: map-get($grey, 'lighten-5') !important;
          box-shadow: none !important;
          td {
            border-left: 2px solid $secondary-lighten-3;
          }
        }
        th:nth-child(2) {
          padding: 0px;
          text-align-last: center;
        }
        td:nth-child(2) {
          background-color: #F4F4F4;
          padding: 0px;
        }
      }
      td {
        .name-n-avatar {
          width: 144px;
        }
      }
      .v-row-group__header {
        background-color: #F5F5FF !important;
        cursor: default;

        td, td:hover {
          border: none !important;
          color: map-get($grey, 'darken-3');
        }
      }
    }
    .v-row-group__header {
      background-color: map-get($nb-azure, 'base') !important;
      cursor: pointer;
      td {
        border-bottom: thin solid map-get($nb-azure, 'darken-1') !important;
      }
    }

    .v-data-table-header {
      th {
        border-top: 1px solid map-get($grey, 'lighten-3');
        color: map-get($grey, 'darken-3') !important;
        &.sortable {
          color: $secondary !important;
        }
      }
    }
  }
  .cell-details {
    transition: width 0.3s;
  }
  .badge-panel {
    &.count {
      .v-badge__wrapper {
        span {
          font-size: 10px;
          height: 16px;
          min-width: 16px;
          width: 16px;
        }
      }
    }
    .v-badge__badge {
      margin-left: -12px;
      padding-top: 4px;
    }
    .status {
      &.no-errors {
        display: none;
      }
    }
  }
  .job-count {
    background-color: $primary-lighten-2;
    border-radius: 4px;
    color: white;
    display: inline-block;
    height: 16px;
    line-height: 13px;
    padding-top: 1px;
    margin-top: -2px;
    min-width: 16px;
    text-align: center;

    &.sitter {
      background-color: $primary-lighten-4;
    }
    &.imbalance {
      background-color: $error !important;
    }
    &.inner-table {
      vertical-align: top;
      margin-top: 1px;
    }
    span {
      font-size: 10px;
    }
  }
  .last-shift-browser {
    .v-card {
      background-color: map-get($grey, 'lighten-5') !important;
      .row .col {
        padding-bottom: 0px;
      }
    }
  }
  .schedule-dropdown {
    border-color: map-get($grey, 'lighten-2');
    justify-content: start;
  }
  .sitter-info {
    span {
      width: 144px;
    }
  }
  .staff-new {
    position: absolute;
    right: -9px;
    bottom: 5px;
  }
  .staff-notes {
    position: absolute;
    right: -10px;
    top: 5px;
  }
  .has-errors {
    position: absolute;
    right: -6px;
    bottom: 8px;
  }
  .job-type-tabs {
    .v-slide-group__content {
      background-color: white;

      .v-tab--active {
        color: map-get($grey, 'darken-3') !important;
      }
    }
  }
  .flag-indicator {
    background-color: $info-lighten-2;
    border-color: $info-lighten-2;
    border-width: 1px;
    margin-top: -23px;
    position: absolute;
    width: 44px;
  }
}
</style>
